// Data-shape helpers only, no navigator.credentials calls of its own.
// Converts between the base64url strings the Rails webauthn-ruby gem
// sends/expects (WebAuthn::Encoder::STANDARD_ENCODING) and the
// ArrayBuffer/BufferSource values the browser's navigator.credentials API
// requires -- server options in, credential response out. Also does the
// one feature-detection check (isPasskeySupported) everything else here
// depends on.

export function isPasskeySupported() {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined"
  );
}

export async function isConditionalMediationAvailable() {
  if (
    !isPasskeySupported() ||
    !window.PublicKeyCredential.isConditionalMediationAvailable
  ) {
    return false;
  }
  try {
    return await window.PublicKeyCredential.isConditionalMediationAvailable();
  } catch (e) {
    return false;
  }
}

function base64urlToBuffer(base64url) {
  const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
  const base64 = (base64url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const buffer = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    buffer[i] = raw.charCodeAt(i);
  }
  return buffer.buffer;
}

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

const withCredentialId = (credentialDescriptor) => ({
  ...credentialDescriptor,
  id: base64urlToBuffer(credentialDescriptor.id),
});

// Server (WebAuthn::Credential.options_for_create, as_json) -> the object
// passed as `navigator.credentials.create({ publicKey })`.
export function creationOptionsFromServer(options) {
  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64urlToBuffer(options.user.id),
    },
    excludeCredentials: (options.excludeCredentials || []).map(
      withCredentialId
    ),
  };
}

// Server (WebAuthn::Credential.options_for_get, as_json) -> the object
// passed as `navigator.credentials.get({ publicKey })`.
export function requestOptionsFromServer(options) {
  return {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    allowCredentials: (options.allowCredentials || []).map(withCredentialId),
  };
}

// The PublicKeyCredential returned by navigator.credentials.create()/.get()
// -> a plain, JSON-serializable object shaped the way
// WebAuthn::Credential.from_create/from_get expect it (see webauthn-ruby's
// AuthenticatorAttestationResponse.from_client /
// AuthenticatorAssertionResponse.from_client).
export function credentialToJSON(credential) {
  const { response } = credential;
  const isRegistration = !!response.attestationObject;

  const json = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults?.() || {},
    response: {
      clientDataJSON: bufferToBase64url(response.clientDataJSON),
    },
  };

  if (credential.authenticatorAttachment) {
    json.authenticatorAttachment = credential.authenticatorAttachment;
  }

  if (isRegistration) {
    json.response.attestationObject = bufferToBase64url(
      response.attestationObject
    );
    json.response.transports = response.getTransports?.() || [];
  } else {
    json.response.authenticatorData = bufferToBase64url(
      response.authenticatorData
    );
    json.response.signature = bufferToBase64url(response.signature);
    if (response.userHandle) {
      json.response.userHandle = bufferToBase64url(response.userHandle);
    }
  }

  return json;
}
